/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useMemo, useEffect, useRef, useState } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { getCSSVariableValue } from '../../../assets/ts/_utils'
import { useThemeMode } from '../../layout/theme-mode/ThemeModeProvider'
import { statusExpiringOrders } from '../../../../api/ordersApi'
import dayjs from 'dayjs'
import 'dayjs/locale/it'
import localizedFormat from 'dayjs/plugin/localizedFormat'

dayjs.extend(localizedFormat)
dayjs.locale('it')

type Props = {
  className: string
}

interface ExpiringOrder {
  ID_Ordine: number
  Nome_Azienda: string
  Importo_Contratto: number
  Data_inizio_contratto: string
  Durata: number
  Data_Scadenza: string
  Numero_visori: number
}

// Estendiamo con un campo "raw" per filtrare / raggruppare sul grafico
interface ExpiringOrderNormalized extends ExpiringOrder {
  Data_Scadenza_raw: string // YYYY-MM-DD
}

type FilterRange = '2settimane' | '1mese' | '2mesi' | '3mesi'

const StatisticheOrdini1: React.FC<Props> = ({ className }) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()

  const [orders, setOrders] = useState<ExpiringOrderNormalized[]>([])
  const [filter, setFilter] = useState<FilterRange>('2mesi')

  // PAGINAZIONE LISTA
  const itemsPerPage = 3
  const [currentPage, setCurrentPage] = useState(1)

  // ===========================
  // FETCH ORDINI IN SCADENZA
  // ===========================
  const fetchData = async () => {
    try {
      const response = await statusExpiringOrders()
      const normalized: ExpiringOrderNormalized[] = response.map((r: ExpiringOrder) => {
        const dataScad = dayjs(r.Data_Scadenza)
        return {
          ...r,
          Data_Scadenza_raw: dataScad.format('YYYY-MM-DD'),
          Data_Scadenza: dataScad.format('LL'),
          Importo_Contratto: Number(r.Importo_Contratto),
        }
      })

      setOrders(normalized)
    } catch (error) {
      console.error('Errore fetch ordini in scadenza:', error)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  // ===========================
  // APPLICAZIONE FILTRO TEMPORALE
  // (guidato dai bottoni 15g / 1m / 2m / 3m)
  // ===========================
  const filteredOrders = useMemo(() => {
    const today = dayjs()

    if (filter === '3mesi') {
      return orders.filter((o) => dayjs(o.Data_Scadenza_raw).isBefore(today.add(3, 'month'), 'day'))
    }

    if (filter === '2mesi') {
      return orders.filter((o) => dayjs(o.Data_Scadenza_raw).isBefore(today.add(2, 'month'), 'day'))
    }

    if (filter === '1mese') {
      return orders.filter((o) => dayjs(o.Data_Scadenza_raw).isBefore(today.add(1, 'month'), 'day'))
    }

    if (filter === '2settimane') {
      return orders.filter((o) => dayjs(o.Data_Scadenza_raw).isBefore(today.add(2, 'week'), 'day'))
    }

    return orders
  }, [orders, filter])

  // ogni volta che cambia il filtro, resettiamo la pagina
  useEffect(() => {
    setCurrentPage(1)
  }, [filter, filteredOrders.length])

  // ===========================
  // TOTALI (Importo / Licenze)
  // basati ESATTAMENTE su filteredOrders
  // ===========================
  const totaleScadenze = useMemo(
    () => filteredOrders.reduce((sum, r) => sum + r.Importo_Contratto, 0),
    [filteredOrders]
  )

  const totaleVisori = useMemo(
    () => filteredOrders.reduce((sum, r) => sum + r.Numero_visori, 0),
    [filteredOrders]
  )

  // ===========================
  // CHART: CREAZIONE / RENDER
  // ===========================
  useEffect(() => {
    if (!chartRef.current) return

    const height = 350
    const chart = new ApexCharts(chartRef.current, getChartOptions(height, filteredOrders))

    chart.render()

    return () => {
      chart.destroy()
    }
  }, [chartRef, mode, filteredOrders])

  // ===========================
  // PAGINAZIONE LISTA
  // ===========================
  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / itemsPerPage))

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const currentData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
  
    // 1) Ordina dal più vicino ad oggi al più lontano (crescente)
    const sorted = [...filteredOrders].sort((a, b) =>
      dayjs(a.Data_Scadenza_raw).valueOf() - dayjs(b.Data_Scadenza_raw).valueOf()
    )
  
    // 2) Paginazione sulla lista già ordinata
    return sorted.slice(start, start + itemsPerPage)
  }, [filteredOrders, currentPage])

  const emptyRows = Array.from({ length: Math.max(0, itemsPerPage - currentData.length) })

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return
    setCurrentPage(page)
  }

  // paginazione "intelligente"
  const getPageNumbers = (total: number, current: number): (number | string)[] => {
    const pages: (number | string)[] = []

    const add = (n: number | string) => pages.push(n)

    add(1)

    if (current > 3) {
      add('...')
    }

    if (current - 1 > 1) {
      add(current - 1)
    }

    if (current !== 1 && current !== total) {
      add(current)
    }

    if (current + 1 < total) {
      add(current + 1)
    }

    if (current < total - 2) {
      add('...')
    }

    if (total > 1) {
      add(total)
    }

    return pages.filter((value, index, self) => self.indexOf(value) === index)
  }

  // ===========================
  // RENDER
  // ===========================
  return (
    <div className={`card ${className}`}>
      {/* HEADER: titolo + filtri temporali del grafico */}
      <div className='card-header border-0 pt-5 d-flex justify-content-between align-items-center'>
        <h3 className='card-title align-items-start'>
          <span className='card-label fw-bold fs-3'>Contratti in scadenza</span>
        </h3>

        <div className='card-toolbar' data-kt-buttons='true'>
        <span className='me-3'>PROSSIMI</span>
          <a
            className={`btn btn-sm btn-color-muted btn-active btn-active-primary px-4 ${
              filter === '2settimane' ? 'active' : ''
            }`}
            onClick={() => setFilter('2settimane')}
          >
            15 giorni
          </a>

          <a
            className={`btn btn-sm btn-color-muted btn-active btn-active-primary px-4 ms-1 ${
              filter === '1mese' ? 'active' : ''
            }`}
            onClick={() => setFilter('1mese')}
          >
            30 giorni
          </a>

          <a
            className={`btn btn-sm btn-color-muted btn-active btn-active-primary px-4 ms-1 ${
              filter === '2mesi' ? 'active' : ''
            }`}
            onClick={() => setFilter('2mesi')}
          >
            2 mesi
          </a>

          <a
            className={`btn btn-sm btn-color-muted btn-active btn-active-primary px-4 ms-1 ${
              filter === '3mesi' ? 'active' : ''
            }`}
            onClick={() => setFilter('3mesi')}
          >
            3 mesi
          </a>
        </div>
      </div>

      {/* BODY: grafico + barra totali + lista + paginazione */}
      <div className='card-body pt-0 d-flex flex-column'>
        {/* GRAFICO */}
        <div ref={chartRef} style={{ height: '350px' }} />

        {/* BARRA TOTALI (stile simile al vecchio StatisticheOrdini1) */}
        <div
          className="px-9 pt-5 pb-5 card-rounded w-100 mt-6"
          style={{
            minHeight: '120px',
            background: 'linear-gradient(135deg, #FFC51A 0%, #000000 100%)',
          }}
        >
          <div className='d-flex justify-content-center align-items-start gap-20'>
            <div className='text-center text-white'>
              <span className='fw-semibold fs-1 d-block'>Importo</span>
              <span className='fw-bold fs-2x pt-1 d-block'>
                €{totaleScadenze.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
              </span>
            </div>

            <div className='text-center text-white'>
              <span className='fw-semibold fs-1 d-block'>N° Licenze</span>
              <span className='fw-bold fs-2x pt-1 d-block'>{totaleVisori}</span>
            </div>
          </div>
        </div>

        {/* LISTA + PAGINAZIONE */}
        <div className='table-responsive px-6 pt-5 pb-3'>
          <table className='table align-middle gs-0 gy-4 mb-0'>
            <tbody>
              {currentData.map((item, index) => (
                <tr key={index}>
                  <td className='min-w-200px'>
                    <div className='fs-5 text-gray-800 fw-bold'>{item.Nome_Azienda}</div>
                    <div className='text-gray-400 fw-semibold fs-7'>{item.Data_Scadenza}</div>
                  </td>

                  <td
                    className='text-gray-800 fw-bold fs-5 text-center'
                    style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                  >
                    {item.Numero_visori} licenze
                  </td>

                  <td
                    className='text-gray-800 fw-bold fs-5 text-end'
                    style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                  >
                    €{item.Importo_Contratto.toLocaleString('it-IT', {
                      minimumFractionDigits: 2,
                    })}
                  </td>
                </tr>
              ))}

              {emptyRows.map((_, i) => (
                <tr key={`empty-${i}`}>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* PAGINAZIONE */}
          <div className='pagination-container d-flex justify-content-center align-items-center mt-3' style={{ fontSize: '0.8rem' }}>
            {/* FRECCIA PRECEDENTE */}
            <button
              className='btn btn-outline-secondary btn-sm me-2'
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              ‹
            </button>

            {/* NUMERI DINAMICI */}
            {getPageNumbers(totalPages, currentPage).map((item, index) =>
              item === '...' ? (
                <span key={index} className='mx-1 fw-bold fs-5'>
                  …
                </span>
              ) : (
                <button
                  key={index}
                  className={`btn btn-outline-secondary btn-sm mx-1 ${
                    currentPage === item ? 'active' : ''
                  }`}
                  onClick={() => handlePageChange(Number(item))}
                >
                  {item}
                </button>
              )
            )}

            {/* FRECCIA SUCCESSIVA */}
            <button
              className='btn btn-outline-secondary btn-sm ms-2'
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export { StatisticheOrdini1 }

// =================== OPTIONS DEL GRAFICO ===================
function getChartOptions(height: number, orders: ExpiringOrderNormalized[]): ApexOptions {
  const labelColor = getCSSVariableValue('--kt-gray-500')
  const borderColor = getCSSVariableValue('--kt-gray-200')
  const baseColor = getCSSVariableValue('--kt-inscadenza')
  const secondaryColor = getCSSVariableValue('--kt-black')

  // Raggruppa per data
  const byDate: {
    [date: string]: { importo: number; licenze: number }
  } = {}

  orders.forEach((o) => {
    const key = o.Data_Scadenza_raw
    if (!byDate[key]) {
      byDate[key] = { importo: 0, licenze: 0 }
    }
    byDate[key].importo += o.Importo_Contratto
    byDate[key].licenze += o.Numero_visori
  })

  const sortedDates = Object.keys(byDate).sort()

  const categories = sortedDates.map((d) => dayjs(d).format('DD/MM/YYYY'))
  const importi = sortedDates.map((d) => byDate[d].importo)
  const licenze = sortedDates.map((d) => byDate[d].licenze)

  return {
    series: [
      {
        name: 'Importo',
        type: 'column',
        data: importi,
      },
      {
        name: 'N° Licenze',
        type: 'line',
        data: licenze,
      },
    ],
    chart: {
      type: 'line',
      height,
      fontFamily: 'inherit',
      zoom: { enabled: false },
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        columnWidth: '40%',
        borderRadius: 4,
      },
    },
    stroke: {
      curve: 'smooth',
      width: [0, 3],
    },
    xaxis: {
      categories,
      labels: {
        style: {
          colors: labelColor,
          fontSize: '14px',
        },
      },
      axisTicks: { show: false },
      axisBorder: { show: false },
      tooltip: { enabled: false }
    },
    yaxis: [
      {
        title: {
          text: 'Importo',
          style: { color: labelColor, fontSize: '16px', fontWeight: 600 },
          offsetX: -15,
        },
        labels: {
          style: { colors: labelColor, fontSize: '14px' },
          formatter: (val) => Math.round(val).toString(),
        },
      },
      {
        opposite: true,
        title: {
          text: 'N° Licenze',
          style: { color: labelColor, fontSize: '16px', fontWeight: 600 },
          offsetX: 12,
        },
        labels: {
          style: { colors: labelColor, fontSize: '14px' },
          formatter: (val) => Math.round(val).toString(),
        },
      },
    ],
    colors: [baseColor, secondaryColor],
    legend: {
      show: true,
      fontSize: '20px',
      offsetY: 0,
    },
    grid: {
      borderColor,
      strokeDashArray: 4,
      yaxis: { lines: { show: true } },
      padding: { bottom: 30 }, // evita taglio legenda
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: [
        {
          // Serie 0 -> Importo
          title: {
            formatter: () => 'Importo:',
          },
          formatter: (val) =>
            val != null ? `€${val.toLocaleString('it-IT', { minimumFractionDigits: 2 })}` : '',
        },
        {
          // Serie 1 -> N° Licenze
          title: {
            formatter: () => 'N° Licenze:',
          },
          formatter: (val) => (val != null ? `${val}` : ''),
        },
      ],
    },
  }
}
